import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Pencil, ShieldAlert, X } from 'lucide-react'
import type { PendingPromptInfo, PromptResponse } from '../../api/types'
import { cn } from '../../lib/cn'
import { Button } from '../ui/Button'
import { Input } from '../ui/Field'
import { Markdown } from './Markdown'
import { isMultiSelect, selectionAnswer, toggleLabel } from './prompt-selection'

/** The header × of a chat question: the same action as the Skip button. */
function SkipClose({ label, disabled, onClick }: { label: string; disabled: boolean; onClick(): void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={label}
      aria-label={label}
      className="mt-0.5 shrink-0 rounded-[6px] p-1 text-ink-500 transition-colors hover:bg-cream-200 hover:text-ink-200 disabled:opacity-50"
    >
      <X className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  )
}

// A choice row is a real `<button>`, not a `<label>` wrapping an input: arrow
// keys move focus between rows, and a click on the row is the whole action.
const ROW =
  'flex w-full items-start gap-2.5 rounded-[10px] border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:border-ginger-500 focus-visible:ring-[3px] focus-visible:ring-ginger-100'
const ROW_OFF = 'border-cream-300 bg-cream-50 hover:border-cream-400'
const ROW_ON = 'border-ginger-500 bg-ginger-100'
const ROW_NUM = 'mt-px w-4 shrink-0 text-right font-mono text-[13px] tabular-nums text-ink-500'
const ROW_TEXT = 'min-w-0 text-sm font-semibold text-ink-100'
const ROW_DESC = 'mt-1 block whitespace-pre-wrap text-xs font-normal text-ink-500'

export function PromptCard({ prompt, onRespond, onSkip }: {
  prompt: PendingPromptInfo
  onRespond(r: PromptResponse): Promise<void>
  /**
   * Chat-only: declining a question ends the turn (the composer's Stop), so the
   * tool reports the question as unanswered and the next turn still sees it.
   * The inbox has no turn to stop — it answers with `allow: false` instead.
   */
  onSkip?(): void
}) {
  const { t } = useTranslation()
  // Labels are model-authored chrome (see the core's `question` tool): use them
  // when present, else the localized defaults. The core sends nothing rather
  // than an English default, so a Vietnamese UI stays Vietnamese.
  const submitLabel = prompt.submitLabel ?? t('chat.prompt.answer')
  const skipLabel = prompt.skipLabel ?? t('chat.prompt.skip')
  const customLabel = prompt.customLabel ?? t('chat.prompt.answerPlaceholder')
  const [busy, setBusy] = useState(false)
  const [custom, setCustom] = useState('')
  const [customOpen, setCustomOpen] = useState(false)
  const [picked, setPicked] = useState<string[]>([])
  const customRef = useRef<HTMLInputElement | null>(null)
  const respond = async (r: PromptResponse) => {
    setBusy(true)
    try { await onRespond(r) } finally { setBusy(false) }
  }
  const skip = () => { if (onSkip) onSkip(); else void respond({ allow: false }) }

  // The free-text row reveals one input instead of keeping it open beside the
  // choices: one action area per card, and nothing to send by accident.
  useEffect(() => {
    if (customOpen) customRef.current?.focus()
  }, [customOpen])

  if (prompt.kind === 'permission') {
    return (
      <div className="overflow-hidden rounded-xl border border-cream-300 bg-cream-50">
        <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
          <ShieldAlert className="h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm font-semibold text-ink-100">
            {t('chat.prompt.permission', { tool: prompt.call?.tool ?? '' })}
          </p>
          {prompt.call && (
            <span className="shrink-0 rounded-[5px] bg-cream-200 px-1.5 py-0.5 font-mono text-[11px] text-ink-300">
              {prompt.call.tool}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-3 p-4">
          {prompt.call && (
            <pre className="max-h-40 overflow-auto rounded-[10px] border border-line bg-cream-100 p-3 font-mono text-[11.5px] leading-relaxed text-ink-300">
              {JSON.stringify(prompt.call.input, null, 2)}
            </pre>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={busy} onClick={() => respond({ allow: true })}>{t('chat.prompt.allowOnce')}</Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => respond({ allow: true, always: true })}>{t('chat.prompt.allowAlways')}</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => respond({ allow: false })}>{t('chat.prompt.deny')}</Button>
          </div>
        </div>
      </div>
    )
  }

  const options = prompt.options ?? []
  const canType = prompt.custom !== false || options.length === 0
  // With no choices the input *is* the card, so it is open from the start.
  const textOnly = options.length === 0
  const typing = textOnly || customOpen
  /**
   * `multiple: true` means "pick any number of these, then answer": a click on a row only toggles
   * it, and one action at the bottom — plus Enter in the text box — sends everything picked. A
   * single-choice prompt keeps the one-click answer it always had, where picking *is* answering.
   */
  const multi = isMultiSelect(prompt)
  const answer = selectionAnswer(picked, custom)
  const send = () => {
    if (multi) { if (answer) void respond({ allow: true, text: answer }) }
    else if (custom.trim()) void respond({ allow: true, text: custom.trim() })
  }

  // Arrows walk every focusable row in the card (choices, the free-text row, the
  // multi-select submit); Tab keeps its native order.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1
      : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0
    if (!step) return
    const rows = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[data-prompt-row]'))
    const at = rows.indexOf(document.activeElement as HTMLElement)
    if (at < 0) return
    e.preventDefault()
    rows[Math.min(rows.length - 1, Math.max(0, at + step))]?.focus()
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Escape') return
    // The composer owns Esc elsewhere; here it closes the free-text row, or
    // declines a text-only question (which has no other affordance in chat).
    e.stopPropagation()
    if (!textOnly) { setCustomOpen(false); return }
    if (onSkip) onSkip()
  }

  return (
    <div className="overflow-hidden rounded-xl border border-cream-300 bg-cream-50">
      <div className="flex items-start gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <Markdown text={prompt.question ?? ''} className="text-sm font-semibold text-ink-100" />
          {multi && <p className="mt-0.5 text-[12.5px] text-ink-500">{t('chat.prompt.multiHint')}</p>}
        </div>
        {onSkip && <SkipClose label={skipLabel} disabled={busy} onClick={skip} />}
      </div>
      <div className="flex flex-col gap-3 p-4" onKeyDown={onKeyDown}>
        {options.length > 0 && (
          <div className="flex flex-col gap-2">
            {options.map((o, i) => {
              const on = multi && picked.includes(o.label)
              return (
                <button
                  key={o.label}
                  type="button"
                  data-prompt-row=""
                  role={multi ? 'checkbox' : undefined}
                  aria-checked={multi ? on : undefined}
                  disabled={busy}
                  title={o.description}
                  onClick={() => (multi ? setPicked(p => toggleLabel(p, o.label)) : void respond({ allow: true, text: o.label }))}
                  className={cn(ROW, on ? ROW_ON : ROW_OFF)}
                >
                  {multi ? (
                    <span
                      className={cn(
                        'mt-px grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border-[1.5px]',
                        on ? 'border-ginger-500 bg-ginger-500' : 'border-cream-400 bg-white'
                      )}
                      aria-hidden="true"
                    >
                      {on && <Check className="h-3 w-3 text-white" strokeWidth={3.5} />}
                    </span>
                  ) : (
                    <span className={ROW_NUM} aria-hidden="true">{i + 1}.</span>
                  )}
                  <span className="min-w-0">
                    <span className={cn('block', ROW_TEXT)}>{o.label}</span>
                    {o.description && <span className={ROW_DESC}>{o.description}</span>}
                  </span>
                </button>
              )
            })}
            {/* The free-text choice is the (n+1)th row, not a second action area. */}
            {canType && (
              <button
                type="button"
                data-prompt-row=""
                disabled={busy}
                aria-expanded={customOpen}
                onClick={() => setCustomOpen(v => !v)}
                className={cn(ROW, customOpen ? ROW_ON : ROW_OFF)}
              >
                {multi ? (
                  <span className="mt-px grid h-[18px] w-[18px] shrink-0 place-items-center text-ink-500" aria-hidden="true">
                    <Pencil className="h-3.5 w-3.5" />
                  </span>
                ) : (
                  <span className={ROW_NUM} aria-hidden="true">{options.length + 1}.</span>
                )}
                <span className="min-w-0 text-sm font-medium text-ink-400">{customLabel}</span>
              </button>
            )}
          </div>
        )}

        {typing && (
          <form
            className="flex flex-col gap-1.5"
            onSubmit={e => { e.preventDefault(); send() }}
          >
            <Input
              ref={customRef}
              autoFocus={textOnly}
              value={custom}
              disabled={busy}
              placeholder={customLabel}
              onChange={e => setCustom(e.target.value)}
              onKeyDown={onInputKeyDown}
            />
            {textOnly && <p className="text-xs text-ink-500">{t('chat.prompt.textHint')}</p>}
          </form>
        )}

        {/* One action row: status + Skip (+ Submit when picking is not answering).
            A text-only chat question needs none of it — × and Esc are enough. */}
        {(!textOnly || !onSkip) && (
          <div className="flex items-center gap-2 border-t border-line pt-3">
            <span className="min-w-0 text-xs text-ink-500">
              {multi
                ? `${picked.length > 0 ? t('chat.prompt.selected', { count: picked.length }) : t('chat.prompt.selectHint')} · ${t('chat.prompt.toggleHint')}`
                : !textOnly && t('chat.prompt.rowHint')}
            </span>
            <span className="flex-1" />
            <Button size="sm" variant="ghost" disabled={busy} onClick={skip}>{skipLabel}</Button>
            {multi && (
              <Button data-prompt-row="" size="sm" disabled={busy || !answer} onClick={send}>{submitLabel}</Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
